import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Droplets, Home, Building, KeyRound } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  LineChart, Line, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { fmt, tooltipStyle } from "./oversiktConstants";

const BK_COMPONENTS = [
  { key: "rente", label: "Rentekostnader", color: "#103766" },
  { key: "vedlikehold", label: "Vedlikehold", color: "#3F8A86" },
  { key: "energi", label: "Energi", color: "#E8A34A" },
  { key: "kommunale_avgifter", label: "Kommunale avgifter", color: "#8AA4BF" },
  { key: "forsikring", label: "Forsikring", color: "#8B5CF6" },
  { key: "eiendomsskatt", label: "Eigedomsskatt", color: "#D35E4E" },
];

const ChartCard = ({ icon: Icon, title, testId, children, source }) => (
  <Card className="bg-white border border-[#E6E9F0] rounded-lg" data-testid={testId}>
    <CardHeader className="border-b border-[#E6E9F0] pb-4">
      <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
        <Icon className="w-5 h-5 text-[#103766]" />
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent className="pt-6">
      <div className="h-[340px]">{children}</div>
      <p className="text-xs text-[#8AA4BF] mt-4 text-center">{source}</p>
    </CardContent>
  </Card>
);

// boligData: { code: { name, gebyrer: [...], bokostnad: {...} } }
const BoligCompareCharts = ({ boligData, colors }) => {
  const entries = Object.entries(boligData || {}).filter(([, k]) => k.gebyrer?.length || k.bokostnad);
  if (entries.length === 0) return null;

  const years = [...new Set(entries.flatMap(([, k]) => (k.gebyrer || []).map((g) => g.year)))].sort();
  const gebyrRows = years.map((year) => {
    const point = { year };
    entries.forEach(([, k]) => {
      const row = k.gebyrer?.find((g) => g.year === year);
      if (row?.sum_arsgebyr != null) point[k.name] = row.sum_arsgebyr;
    });
    return point;
  });

  const bkRows = entries
    .filter(([, k]) => k.bokostnad)
    .map(([, k]) => {
      const point = { name: k.name, total: k.bokostnad.total };
      BK_COMPONENTS.forEach((c) => { point[c.label] = k.bokostnad.components?.[c.key] ?? 0; });
      return point;
    });
  const bkYear = entries.find(([, k]) => k.bokostnad)?.[1]?.bokostnad?.year;

  const bpRows = entries
    .filter(([, k]) => k.boligpriser)
    .map(([, k]) => ({
      name: k.name,
      period: `${k.boligpriser.year} K${k.boligpriser.quarter}`,
      "Alle bustadtypar": k.boligpriser.alle?.kvm_pris ?? null,
      Einebustader: k.boligpriser.eneboliger?.kvm_pris ?? null,
      Småhus: k.boligpriser.smahus?.kvm_pris ?? null,
      Blokkleilegheiter: k.boligpriser.blokkleiligheter?.kvm_pris ?? null,
    }));
  const leieRows = entries
    .filter(([, k]) => k.leie)
    .map(([, k]) => ({ name: k.name, "Leige 2-roms": k.leie.monthly, zone: k.leie.zone_label, estimate: k.leie.estimate, year: k.leie.year }));
  const anyRentEstimate = leieRows.some((r) => r.estimate);

  return (
    <>
      {gebyrRows.length > 0 && (
        <ChartCard icon={Droplets} title="Sum kommunale årsgebyr for bustad (kr)" testId="compare-gebyr-chart" source="Kjelde: SSB 12842 – vatn, avløp, avfall og feiing, standardbustad ekskl. mva">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={gebyrRows}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
              <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} />
              <YAxis stroke="#8AA4BF" fontSize={12} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => `${fmt(v)} kr`} contentStyle={tooltipStyle} />
              <Legend />
              {entries.map(([code, k], idx) => (
                <Line key={code} type="monotone" dataKey={k.name} stroke={colors[idx % colors.length]} strokeWidth={2} dot={{ fill: colors[idx % colors.length], r: 4 }} connectNulls />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      )}
      {bkRows.length > 0 && (
        <ChartCard icon={Home} title={`Bokostnad ${bkYear} – standard einebustad 120 m² (kr per år)`} testId="compare-bokostnad-chart" source="Kjelde: Huseiernes bokostnadsindeks (Samfunnsøkonomisk analyse)">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={bkRows} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
              <XAxis dataKey="name" stroke="#8AA4BF" fontSize={12} />
              <YAxis stroke="#8AA4BF" fontSize={12} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                formatter={(v) => `${fmt(v)} kr`}
                contentStyle={tooltipStyle}
                labelFormatter={(label, payload) => `${label} – totalt ${fmt(payload?.[0]?.payload?.total)} kr`}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {BK_COMPONENTS.map((c) => (
                <Bar key={c.key} dataKey={c.label} stackId="bk" fill={c.color} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}
      {bpRows.length > 0 && (
        <ChartCard icon={Building} title={`Kvadratmeterpris omsatte bustader ${bpRows[0].period} (kr/m²)`} testId="compare-boligpriser-chart" source="Kjelde: SSB 14310 – gjennomsnittleg kvadratmeterpris per bustadtype, siste kvartal">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={bpRows} barCategoryGap="25%">
              <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
              <XAxis dataKey="name" stroke="#8AA4BF" fontSize={12} />
              <YAxis stroke="#8AA4BF" fontSize={12} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => `${fmt(v)} kr/m²`} contentStyle={tooltipStyle} />
              <Legend />
              <Bar dataKey="Alle bustadtypar" fill="#1F2A36" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Einebustader" fill="#103766" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Småhus" fill="#3F8A86" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Blokkleilegheiter" fill="#D35E4E" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}
      {leieRows.length > 0 && (
        <ChartCard
          icon={KeyRound}
          title={<>Gjennomsnittleg månadsleige toromsbustad {leieRows[0].year} (kr){anyRentEstimate && <Badge variant="outline" className="ml-2 border-[#E8A34A] text-[#B8741F] bg-[#FDF6EA] text-[10px] uppercase">Estimat</Badge>}</>}
          testId="compare-leie-chart"
          source={`Kjelde: SSB 09895 (leigemarknadsundersøkinga), per prissone. ${anyRentEstimate ? "Prissone anslått ut frå folketal for kommunar utan eigen sone." : ""}`}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={leieRows} barCategoryGap="35%">
              <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
              <XAxis dataKey="name" stroke="#8AA4BF" fontSize={12} />
              <YAxis stroke="#8AA4BF" fontSize={12} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v, n, item) => [`${fmt(v)} kr/mnd`, `${item.payload.zone}${item.payload.estimate ? " (estimat)" : ""}`]} contentStyle={tooltipStyle} />
              <Bar dataKey="Leige 2-roms" radius={[4, 4, 0, 0]}>
                {leieRows.map((r, idx) => <Cell key={r.name} fill={colors[idx % colors.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}
    </>
  );
};

export default BoligCompareCharts;
